import { GlobalContext } from "components/GlobalContext";
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Chart } from 'react-chartjs-2';
import annotationPlugin from "chartjs-plugin-annotation";
import { formatDateTime, isoDateTimeStringWithoutTimeZone, numberWithCommas } from "utilities/StringUtility";
import moment from "moment";

import {
    Chart as ChartJS,
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController
} from 'chart.js';

ChartJS.register(
    LinearScale,
    CategoryScale,
    BarElement,
    PointElement,
    LineElement,
    Legend,
    Tooltip,
    LineController,
    BarController,
    annotationPlugin
);

const measurements = [
    "Particulate Matter 1.0 microns|pm1_0",
    "Particulate Matter 2.5 microns|pm2_5|12",
    "Particulate Matter 10 microns|pm10",
    "Carbon Dioxide|co2|900",
    "Formaldehyde|hcho|0.1",
    "Total Volatile Organic Compounds|tvoc|0.5"
]

interface IAirQualityEntry {
    date: Date;
    pm1_0: number;
    pm2_5: number;
    pm10: number;
    co2: number;
    hcho: number;
    tvoc: number;
}

const averageMethods = ["Every Half Hour", "Hourly", "Every 6 Hours", "Daily", "Weekly", "Monthly"];
const zoomLevels = [1, 2, 4, 10];

interface IAirQualityGraphFilter {
    dateStart?: string;
    dateEnd?: string;
    measurements?: string[];
    averageMethod?: string;
    combinedGraph?: boolean;
}

interface AQGrapherProps {
}

const AQGrapher = (props: AQGrapherProps) => {
    const graphColors = ["#FABE61", "#E36492", "#6163FA", "#40B7F7", "#51FF82", "#E6E645", "#FCB647", "#F53125", "#DAF720", "#FF8A30", "#C927E6", "#26AEFC", "#54F527", "#FABE61", "#E36492", "#6163FA", "#5DF0BD", "#40B7F7", "#51FF82", "#E6E645", "#FCB647", "#F53125", "#DAF720", "#FF8A30", "#C927E6", "#26AEFC", "#54F527"];
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [isDragging, setIsDragging] = useState(false);
    const [aqEntries, setAQEntries] = useState<IAirQualityEntry[]>([]);
    const [graphFilter, setGraphFilter] = useState<IAirQualityGraphFilter | undefined>();
    const [reportType, setReportType] = useState(0);
    const [zoomLevel, setZoomLevel] = useState(1);

    useEffect(
        () => {
            (async () => {
            })();
        }, []);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop =
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsDragging(false);

            const file = event.dataTransfer.files[0];
            if (file && file.type === 'text/plain') {
                const reader = new FileReader();

                reader.onload = (e) => {
                    const content = e.target?.result as string;
                    const entries = parseAQData(content);
                    setGraphFilter(undefined);
                    setAQEntries(entries);
                };

                reader.readAsText(file);
            } else {
                alert('Please drop a .txt file.');
            }
        };

    const parseAQData = (content: string): IAirQualityEntry[] => {
        const result: IAirQualityEntry[] = [];
        const lines = content.split('\n').filter(line => line.trim() !== '');

        lines.forEach(line => {
            const parts = line.split(' ');

            const datePart = parts.find(part => part.startsWith('Date:'))?.split(':')[1] ?? '';
            const timePart = parts.find(part => part.startsWith('Time:'))?.split(':')[1] ?? '';
            const pm1_0 = parseInt(parts.find(part => part.startsWith('PM1.0:'))?.split(':')[1] ?? '0');
            const pm2_5 = parseInt(parts.find(part => part.startsWith('PM2.5:'))?.split(':')[1] ?? '0');
            const pm10 = parseInt(parts.find(part => part.startsWith('PM10:'))?.split(':')[1] ?? '0');
            const co2 = parseInt(parts.find(part => part.startsWith('CO2:'))?.split(':')[1] ?? '0');
            const hcho = parseFloat(parts.find(part => part.startsWith('HCHO:'))?.split(':')[1] ?? '0');
            const tvoc = parseFloat(parts.find(part => part.startsWith('TVOC:'))?.split(':')[1] ?? '0');
            const parsedMoment = moment(`${datePart}${timePart}`, 'YYYYMMDDHHmm');

            const entry: IAirQualityEntry = {
                date: parsedMoment.toDate(),
                pm1_0,
                pm2_5,
                pm10,
                co2,
                hcho,
                tvoc
            };

            result.push(entry);
        });

        return result;
    };

    const showGraphFilters = () => {
        const tempReportFilter = { ...graphFilter };

        if (!tempReportFilter.measurements) {
            tempReportFilter.measurements = ["pm2_5", "co2", "hcho", "tvoc"];
        }

        if (!tempReportFilter.averageMethod) {
            tempReportFilter.averageMethod = "Daily";
        }

        if (!tempReportFilter.dateStart) {
            tempReportFilter.dateStart = moment(aqEntries[0].date).toISOString();
        }

        if (!tempReportFilter.dateEnd) {
            tempReportFilter.dateEnd = moment(aqEntries[aqEntries.length - 1].date).toISOString();
        }

        if (!tempReportFilter.combinedGraph) {
            tempReportFilter.combinedGraph = true;
        }

        globalContext.showModal({
            title: `Select filters`,
            cancelButtonTitle: "Close",
            formElements: [
                {
                    key: "dateStart",
                    type: "datetime",
                    title: "Start Time",
                    value: (tempReportFilter?.dateStart || moment().utc().startOf("day").set("hour", 0).toISOString()).slice(0, 16)
                },
                {
                    key: "dateEnd",
                    type: "datetime",
                    title: "End Time",
                    value: (tempReportFilter?.dateEnd || moment().utc().add("day", 1).startOf("day").set("hour", 0).set("minute", 59).toISOString()).slice(0, 16)
                },
                {
                    key: "averageMethod",
                    type: "select",
                    options: averageMethods.map(m => ({ title: m, value: m })),
                    title: "Average Method",
                    value: tempReportFilter.averageMethod || "Daily"
                },
                {
                    key: "combinedGraph",
                    type: "select",
                    options: [{ title: "No", value: "false" }, { title: "Yes", value: "true" }],
                    title: "Combine Graphs (values will be scaled to fit on the same graph)",
                    value: (tempReportFilter.combinedGraph ?? true).toString()
                },
                {
                    key: "extraMeasurementIds",
                    type: "adder",
                    title: "Measurements Averaged by Batch",
                    value: (tempReportFilter.measurements ?? [])?.join(","),
                    useFilter: true,
                    options:
                        measurements
                            .map(
                                m => ({
                                    title: m.split("|")[0],
                                    value: m.split("|")[1]
                                })),
                    style: {
                        width: "100%",
                        height: "140px"
                    }
                },
                {
                    key: "zoomLevel",
                    type: "select",
                    options: zoomLevels.map(m => ({ title: m + "X", value: m.toString() })),
                    title: "Zoom Level",
                    value: zoomLevel.toString()
                },],
            content: <><strong>PLEASE NOTE:</strong> The more specific you are with these filters, the faster your report will generate!</>,
            okButtonTitle: "Build Graph",
            okCallback: (data: any) => {
                const daySpanned = moment(data[1].value).diff(moment(data[0].value), 'day');
                setReportType(0);
                var newReportFilter = {
                    dateStart: isoDateTimeStringWithoutTimeZone(data[0].value),
                    dateEnd: isoDateTimeStringWithoutTimeZone(data[1].value),
                    averageMethod: data[2].value,
                    combinedGraph: data[3].value === "true",
                    measurements: data[4].value.split(",")
                };

                setZoomLevel(parseInt(data[5].value));
                setGraphFilter(newReportFilter);
                console.log(newReportFilter);
            }
        })
    };

    const getAveragedData = (entries: IAirQualityEntry[], filter?: IAirQualityGraphFilter) => {
        if (entries.length === 0 || !filter) {
            return [];
        }

        const groupedEntries: IAirQualityEntry[][] = [];
        let currentGroup: IAirQualityEntry[] = [];
        let currentGroupStart = moment(entries[0].date);

        entries.forEach((entry) => {
            const entryTime = moment(entry.date);
            let shouldGroup = false;

            switch (filter.averageMethod) {
                case 'Every Half Hour':
                    shouldGroup = entryTime.isSame(currentGroupStart, 'hour') && entryTime.minute() < 30;
                    break;
                case 'Hourly':
                    shouldGroup = entryTime.isSame(currentGroupStart, 'hour');
                    break;
                case 'Every 6 Hours':
                    shouldGroup = entryTime.isSame(currentGroupStart, 'hour') && entryTime.hour() % 6 === 0;
                    break;
                case 'Daily':
                    shouldGroup = entryTime.isSame(currentGroupStart, 'day');
                    break;
                case 'Weekly':
                    shouldGroup = entryTime.isSame(currentGroupStart, 'week');
                    break;
                case 'Monthly':
                    shouldGroup = entryTime.isSame(currentGroupStart, 'month');
                    break;
            }

            if (shouldGroup) {
                currentGroup.push(entry);
            } else {
                groupedEntries.push(currentGroup);
                currentGroup = [entry];
                currentGroupStart = entryTime;
            }
        });

        // Push the last group
        if (currentGroup.length > 0) {
            groupedEntries.push(currentGroup);
        }

        // Calculate averages for each group
        const averagedEntries: (IAirQualityEntry | undefined)[] = groupedEntries.map((group) => {
            if (!group || group.length === 0) {
                return undefined;
            }

            const firstEntry = group[0];

            const average = (field: keyof IAirQualityEntry): number =>
                group.reduce((acc, entry) => acc + parseFloat((entry[field] ?? "0").toString()), 0) / group.length;

            return ({
                date: firstEntry.date,
                pm1_0: average('pm1_0'),
                pm2_5: average('pm2_5'),
                pm10: average('pm10'),
                co2: average('co2'),
                hcho: average('hcho'),
                tvoc: average('tvoc')
            } as IAirQualityEntry);
        });

        return averagedEntries.filter(entry => !!entry) as IAirQualityEntry[];
    };

    const findGaps = (entries: IAirQualityEntry[], gapSizeInHours: number) => {
        if (gapSizeInHours == 0 || entries.length == 0) {
            return [];
        }

        const gaps = [];
        const timespanMilliseconds = (gapSizeInHours * 1.05) * 60 * 60 * 1000;

        for (let i = 1; i < entries.length; i++) {
            const prevDate = new Date(entries[i - 1].date).getTime();
            const currentDate = new Date(entries[i].date).getTime();
            const timeDifference = currentDate - prevDate;

            if (timeDifference > timespanMilliseconds) {
                gaps.push({
                    start: entries[i - 1].date,
                    end: entries[i].date,
                    startIndex: i - 1,
                    endIndex: i
                });
            }
        }

        return gaps;
    };

    const doSingleGraphValueScaling = (entry: IAirQualityEntry, measurement: string, defaultValue: number): number => {
        if (!entry || !measurement) {
            return defaultValue;
        }

        switch (measurement) {
            case "co2":
                return entry[measurement as keyof IAirQualityEntry] as number / 30;
            case "tvoc":
                return entry[measurement as keyof IAirQualityEntry] as number * 20;
            case "hcho":
                return entry[measurement as keyof IAirQualityEntry] as number * 300;
            default:
                return entry[measurement as keyof IAirQualityEntry] as number;
        }
    };

    const renderCharts = (entries: IAirQualityEntry[], filter?: IAirQualityGraphFilter) => {
        if (!filter) {
            return <></>;
        }

        const data = entries.filter(d => moment(d.date).isBetween(moment(filter.dateStart).startOf('day'), moment(filter.dateEnd).endOf('day')));
        const averagedData = getAveragedData(data, filter);
        let returnContent: ReactElement[] = [];

        var gapSize = 0;
        switch (filter.averageMethod) {
            case "Every Half Hour":
                gapSize = 0.5;
                break;

            case "Hourly":
                gapSize = 1;
                break;

            case "Every 6 Hours":
                gapSize = 6;
                break;

            case "Daily":
                gapSize = 24;
                break;

            case "Weekly":
                gapSize = 168;
                break;

            case "Monthly":
                gapSize = 720;
                break;
        }

        const gaps = findGaps(averagedData, gapSize);

        if (filter.combinedGraph) {
            const lowestY = Math.min(...averagedData.map(d => Math.min(...filter.measurements?.map(m => (doSingleGraphValueScaling(d, m, 999))) || [])));
            const highestY = Math.max(...averagedData.map(d => Math.max(...filter.measurements?.map(m => (doSingleGraphValueScaling(d, m, 0))) || [])));

            const gapAnnotations: any =
                gaps.map(
                    (gap, index) => ({
                        ["gap" + index]: {
                            drawTime: 'afterDatasetsDraw',
                            type: 'box',
                            xMin: gap.startIndex,
                            xMax: gap.endIndex,
                            yMin: lowestY,
                            yMax: highestY,
                            backgroundColor: 'rgba(255, 99, 132, 0.25)',
                            borderColor: 'rgba(255, 99, 132, 0.25)',
                            borderWidth: 10
                        }
                    }));

            const labels = averagedData.map(d => formatDateTime(d.date));
            const chartData: any = {
                labels,
                datasets: [
                    ...filter.measurements?.map((m, index) => {
                        return {
                            type: 'line' as const,
                            label: m,
                            backgroundColor: graphColors[index],
                            borderColor: graphColors[index],
                            fill: false,
                            borderWidth: 2,
                            data: averagedData.map(d => doSingleGraphValueScaling(d, m, 0))
                        }
                    }) || []
                ]
            };

            return (
                <>
                    <h2 className="text-center" style={{ width: "100%" }}>{numberWithCommas(data.length)} entries averaged {filter.averageMethod?.toLocaleLowerCase()} to {numberWithCommas(averagedData.length)}</h2>
                    <p className="text-center" style={{ width: "100%" }}><strong><i>Y Axis is not accurate! Values normalized for scale.</i></strong></p>
                    <div className="mb-3" style={{ height: "800px", width: "100%", position: "relative" }}>
                        <Chart
                            type='line'
                            data={chartData}
                            height="800px"
                            options={{
                                maintainAspectRatio: false,
                                animation: false,
                                scales: {
                                    x: {
                                        ticks: {
                                            autoSkip: true,
                                            maxTicksLimit: 30 * zoomLevel
                                        }
                                    }
                                }, // https://www.chartjs.org/chartjs-plugin-annotation/latest/guide/types/box.html
                                plugins:
                                    gapAnnotations ? {
                                        annotation: {
                                            annotations: Object.assign({}, ...gapAnnotations)
                                        }
                                    } : undefined
                            }} />
                    </div >
                </>
            );
        } else {

            filter.measurements?.forEach(
                (measurement, index) => {
                    const originalMeasurementEntry = measurements.find(m => m.split("|")[1] === measurement)?.split("|");
                    const labels = averagedData.map(d => formatDateTime(d.date));
                    const chartData: any = {
                        labels,
                        datasets: [
                            {
                                type: 'line' as const,
                                label: measurement,
                                backgroundColor: graphColors[index],
                                borderColor: graphColors[index],
                                fill: false,
                                borderWidth: 2,
                                data: averagedData.map((d: IAirQualityEntry) => d[measurement as keyof IAirQualityEntry])
                            }
                        ]
                    };

                    const lowestY = Math.min(...averagedData.map(d => d[measurement as keyof IAirQualityEntry] as number));
                    const highestY = Math.max(...averagedData.map(d => d[measurement as keyof IAirQualityEntry] as number));

                    const gapAnnotations: any =
                        gaps.map(
                            (gap, index) => ({
                                ["gap" + index]: {
                                    drawTime: 'afterDatasetsDraw',
                                    type: 'box',
                                    xMin: gap.startIndex,
                                    xMax: gap.endIndex,
                                    yMin: lowestY,
                                    yMax: highestY,
                                    backgroundColor: 'rgba(255, 99, 132, 0.25)',
                                    borderColor: 'rgba(255, 99, 132, 0.25)',
                                    borderWidth: 10
                                }
                            }));

                    let lineAnnotations = {};
                    if (originalMeasurementEntry && originalMeasurementEntry.length > 2) {
                        lineAnnotations = {
                            "line1": {
                                type: 'line',
                                yMin: parseFloat(originalMeasurementEntry[2]),
                                yMax: parseFloat(originalMeasurementEntry[2]),
                                borderColor: 'rgb(100, 99, 255)',
                                borderWidth: 2,
                            }
                        };
                    }

                    returnContent.push(
                        <>
                            <h2 className="text-center" style={{ width: "100%" }}>{numberWithCommas(data.length)} entries for {measurement} averaged {filter.averageMethod?.toLocaleLowerCase()} to {numberWithCommas(averagedData.length)}  </h2>
                            <div className="mb-3" style={{ height: "300px", width: "100%", position: "relative" }}>
                                <Chart
                                    type='line'
                                    data={chartData}
                                    height="300px"
                                    options={{
                                        maintainAspectRatio: false,
                                        animation: false,
                                        borderColor: 'black',
                                        scales: {
                                            x: {
                                                ticks: {
                                                    autoSkip: true,
                                                    maxTicksLimit: 30 * zoomLevel
                                                }
                                            }
                                        }, // https://www.chartjs.org/chartjs-plugin-annotation/latest/guide/types/box.html
                                        plugins:
                                        {
                                            annotation: {
                                                annotations: {
                                                    ...Object.assign({}, ...gapAnnotations),
                                                    ...lineAnnotations
                                                }
                                            }
                                        }
                                    }} />
                            </div>
                        </>
                    );
                });
        }

        return returnContent;
    };

    return (
        <>
            <Row>
                <Col>
                    <Card.Body style={{ "width": (zoomLevel * 100) + "vw" }}>
                        <Card.Title>
                            Air Quality Grapher
                            {
                                aqEntries && aqEntries.length > 0 &&
                                <>
                                    <br />{numberWithCommas(aqEntries.length)} entries.
                                </>
                            }
                        </Card.Title>

                        {
                            !aqEntries || aqEntries.length === 0 ?
                                <Row className="mt-2">
                                    <Col>
                                        <div
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            style={{
                                                border: '2px dashed gray',
                                                padding: '50px',
                                                textAlign: 'center',
                                                backgroundColor: isDragging ? '#f0f0f0' : '#fff',
                                            }}
                                        >
                                            {isDragging ? 'Drop the .TXT file here...' : 'Drop the .TXT file here'}
                                        </div>
                                    </Col>
                                </Row> :
                                <>
                                    <Row className="mt-2">
                                        <Col className="d-grid gap-2">
                                            <button
                                                className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14"
                                                onClick={() => showGraphFilters()}>
                                                Edit Filters
                                            </button>
                                        </Col>
                                    </Row>
                                    <Row className="mt-2">
                                        <Col>
                                            {
                                                !graphFilter ?
                                                    <>
                                                        Please select filters to generate a graph.
                                                    </>
                                                    :
                                                    <>
                                                        {
                                                            renderCharts(aqEntries, graphFilter)
                                                        }
                                                    </>
                                            }
                                        </Col>
                                    </Row>
                                </>
                        }

                    </Card.Body>
                </Col>
            </Row>
        </>
    );
}

export default AQGrapher;
